/*
 * HomeLand: logo customisation on the product page (blocks/hl-customise.liquid).
 *
 * While the "Add your logo or branding" switch is on, this takes over Add to cart for the
 * product form in the same section (the main button, the Enter key and the sticky bar, which
 * clicks the main button). It validates the panel, then sends ONE multipart /cart/add.js call:
 *   items[0]  the garment, with line item properties (Branding, Placement(s), Badge, Notes,
 *             Logo and Badge artwork as uploaded files, and a hidden _hl_group id)
 *   items[1+] each add-on variant at the same quantity, nested under the garment (parent_id),
 *             with the properties For and _hl_group
 * It dispatches Horizon's CartLinesUpdateEvent once, so the cart drawer opens once and morphs
 * from the returned sections, exactly as a normal add does. With the switch off, Horizon's own
 * add to cart runs untouched. No libraries.
 */
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';
import { formatMoney } from '@theme/money-formatting';

const ACCEPTED = ['png', 'jpg', 'jpeg', 'pdf', 'ai', 'eps', 'svg'];
const MSG = {
  nothing: 'Choose at least one placement or a badge, or switch branding off.',
  logo: 'Upload your logo for the placements you chose.',
  badgeFile: 'Upload the artwork for your custom badge.',
  type: 'That file type is not accepted. Use PNG, JPG, PDF, AI, EPS or SVG.',
  size: (mb) =>
    `That file is over ${mb} MB. Choose a smaller file, or email it to admin@homelandbrand.co.za after ordering, quoting your order number.`,
  form: 'Please check the highlighted branding fields.',
  added: (q) => `Added ${q} with your branding. Our team will confirm the artwork before production.`,
  failed: 'We could not add this to your cart. Please try again, or call 069 007 2378.',
};

class HlCustomise extends HTMLElement {
  #abort = new AbortController();
  #busy = false;

  connectedCallback() {
    const { signal } = this.#abort;
    this.section = this.closest('.shopify-section') || document;
    this.toggle = this.querySelector('[data-hl-cz-toggle]');
    this.panel = this.querySelector('[data-hl-cz-panel]');
    this.badge = this.querySelector('[data-hl-cz-badge]');
    this.logo = this.querySelector('[data-hl-cz-logo]');
    this.badgeFile = this.querySelector('[data-hl-cz-badge-file]');
    this.notes = this.querySelector('[data-hl-cz-notes]');
    this.maxMb = Number(this.dataset.maxMb) || 10;

    this.toggle?.addEventListener('change', () => this.#onToggle(), { signal });
    this.addEventListener('change', (e) => this.#onChange(e), { signal });

    // Quantity changes (the stepper buttons and typing) update the live price.
    this.section.addEventListener('input', (e) => this.#onQuantityInput(e), { signal });
    this.section.addEventListener('change', (e) => this.#onQuantityInput(e), { signal });
    document.addEventListener('quantity-selector:update', () => this.#render(), { signal });

    // Capture phase on window: Horizon delegates on:click and on:submit from document in the
    // capture phase, so only a window capture listener runs before it.
    window.addEventListener('click', (e) => this.#onClickCapture(e), { capture: true, signal });
    window.addEventListener('submit', (e) => this.#onSubmitCapture(e), { capture: true, signal });

    // A restored page (back/forward cache) can come back with the switch already on.
    if (this.toggle?.checked) this.#onToggle();
    this.#render();
  }

  disconnectedCallback() {
    this.#abort.abort();
  }

  get active() {
    return Boolean(this.toggle?.checked);
  }

  get form() {
    return /** @type {HTMLFormElement | null} */ (
      this.section.querySelector('product-form-component form[data-type="add-to-cart-form"]')
    );
  }

  get method() {
    const fixed = /** @type {HTMLInputElement | null} */ (this.querySelector('[data-hl-cz-method-fixed]'));
    if (fixed) return fixed.value;
    const checked = /** @type {HTMLInputElement | null} */ (this.querySelector('[data-hl-cz-method]:checked'));
    return checked?.value || 'embroidery';
  }

  get placementGroup() {
    return /** @type {HTMLFieldSetElement | null} */ (
      this.querySelector(`[data-hl-cz-placements="${this.method}"]`)
    );
  }

  get quantity() {
    const input = /** @type {HTMLInputElement | null} */ (this.form?.querySelector('input[name="quantity"]'));
    const q = parseInt(input?.value || '1', 10);
    return Number.isFinite(q) && q > 0 ? q : 1;
  }

  /** @returns {Array<{id: string, price: number, title: string, addonTitle: string, kind: string}>} */
  get selections() {
    const out = [];
    const group = this.placementGroup;
    if (group) {
      for (const input of group.querySelectorAll('[data-hl-cz-placement]:checked')) {
        const el = /** @type {HTMLInputElement} */ (input);
        out.push({
          id: el.value,
          price: Number(el.dataset.price) || 0,
          title: el.dataset.title || '',
          addonTitle: el.dataset.addonTitle || '',
          kind: 'placement',
        });
      }
    }
    const opt = this.#badgeOption();
    if (opt) {
      out.push({
        id: opt.value,
        price: Number(opt.dataset.price) || 0,
        title: opt.dataset.title || '',
        addonTitle: opt.dataset.addonTitle || '',
        kind: 'badge',
      });
      if (opt.dataset.custom === 'true' && this.dataset.setupId) {
        out.push({
          id: this.dataset.setupId,
          price: Number(this.dataset.setupPrice) || 0,
          title: this.dataset.setupTitle || 'Logo File Setup',
          addonTitle: this.dataset.setupTitle || 'Logo File Setup',
          kind: 'setup',
        });
      }
    }
    return out;
  }

  #badgeOption() {
    const sel = /** @type {HTMLSelectElement | null} */ (this.badge);
    if (!sel || !sel.value) return null;
    return /** @type {HTMLOptionElement} */ (sel.selectedOptions[0]);
  }

  #money(cents) {
    return formatMoney(Math.round(cents), this.dataset.moneyFormat || 'R {{amount}}', this.dataset.currency || 'ZAR');
  }

  #onToggle() {
    const on = this.active;
    this.toggle?.setAttribute('aria-checked', String(on));
    if (this.panel) this.panel.hidden = !on;
    if (!on) this.#clearErrors();
    this.#render();
  }

  /** @param {Event} e */
  #onChange(e) {
    const t = /** @type {HTMLElement} */ (e.target);
    if (t.matches('[data-hl-cz-method]')) {
      for (const group of this.querySelectorAll('[data-hl-cz-placements]')) {
        const fs = /** @type {HTMLFieldSetElement} */ (group);
        const on = fs.dataset.hlCzPlacements === this.method;
        fs.hidden = !on;
        fs.disabled = !on;
      }
    }
    if (t.matches('[data-hl-cz-placement]')) this.#syncPockets();
    if (t.matches('[data-hl-cz-badge]')) {
      const custom = this.#badgeOption()?.dataset.custom === 'true';
      const field = /** @type {HTMLElement | null} */ (this.querySelector('[data-hl-cz-badge-file-field]'));
      if (field) field.hidden = !custom;
    }
    if (t.matches('input[type="file"]')) this.#checkFile(/** @type {HTMLInputElement} */ (t), false);
    // Clear an error as soon as the customer acts on it.
    if (this.dataset.validated === 'true') this.#validate(false);
    this.#render();
  }

  /** @param {Event} e */
  #onQuantityInput(e) {
    const t = /** @type {HTMLElement} */ (e.target);
    if (t instanceof HTMLInputElement && t.name === 'quantity' && this.form?.contains(t)) this.#render();
  }

  /** "Both Sides, Pocket Size" covers the two single pockets, so it switches them off. */
  #syncPockets() {
    const group = this.placementGroup;
    if (!group) return;
    const both = /** @type {HTMLInputElement | null} */ (group.querySelector('[data-pocket="both"]'));
    const singles = /** @type {HTMLInputElement[]} */ ([...group.querySelectorAll('[data-pocket="single"]')]);
    if (both) {
      for (const s of singles) {
        if (both.checked) s.checked = false;
        s.disabled = both.checked;
      }
    }
    const tip = /** @type {HTMLElement | null} */ (group.querySelector('[data-hl-cz-pocket-tip]'));
    if (tip) tip.hidden = !(both && singles.length > 1 && singles.every((s) => s.checked));
  }

  #render() {
    const list = this.querySelector('[data-hl-cz-lines]');
    const total = this.querySelector('[data-hl-cz-total]');
    const req = /** @type {HTMLElement | null} */ (this.querySelector('[data-hl-cz-logo-req]'));
    const sel = this.selections;
    if (req) req.hidden = !sel.some((s) => s.kind === 'placement');
    if (!list || !total) return;

    list.replaceChildren(
      ...sel.map((s) => {
        const li = document.createElement('li');
        const a = document.createElement('span');
        const b = document.createElement('span');
        a.textContent = s.kind === 'placement' ? `${this.#methodLabel()}, ${s.title}` : s.title;
        b.textContent = this.#money(s.price);
        li.append(a, b);
        return li;
      })
    );

    if (this.emptyText === undefined) this.emptyText = total.textContent || '';
    if (sel.length === 0) {
      total.textContent = this.emptyText;
      return;
    }
    const each = sel.reduce((sum, s) => sum + s.price, 0);
    const q = this.quantity;
    total.textContent = `Branding: ${this.#money(each)} x ${q} = ${this.#money(each * q)}`;
  }

  #methodLabel() {
    return this.placementGroup?.dataset.methodLabel || 'Embroidery';
  }

  /**
   * @param {HTMLInputElement} input
   * @param {boolean} required
   * @returns {string} the error message, or '' when valid
   */
  #checkFile(input, required) {
    const file = input.files?.[0];
    if (!file) return required ? 'missing' : '';
    const ext = (file.name.split('.').pop() || '').toLowerCase();
    if (!ACCEPTED.includes(ext)) return MSG.type;
    if (file.size > this.maxMb * 1024 * 1024) return MSG.size(this.maxMb);
    return '';
  }

  #clearErrors() {
    for (const el of this.querySelectorAll('[data-hl-cz-error]')) {
      /** @type {HTMLElement} */ (el).hidden = true;
      el.textContent = '';
    }
    for (const el of this.querySelectorAll('[aria-invalid]')) el.removeAttribute('aria-invalid');
    delete this.dataset.validated;
  }

  /**
   * @param {HTMLElement | null} errorEl
   * @param {HTMLElement | null} field
   * @param {string} message
   */
  #setError(errorEl, field, message) {
    if (errorEl) {
      errorEl.textContent = message;
      errorEl.hidden = !message;
    }
    if (field) {
      if (message) field.setAttribute('aria-invalid', 'true');
      else field.removeAttribute('aria-invalid');
    }
  }

  /**
   * @param {boolean} [focus]
   * @returns {boolean}
   */
  #validate(focus = true) {
    this.dataset.validated = 'true';
    const sel = this.selections;
    const hasPlacement = sel.some((s) => s.kind === 'placement');
    const customBadge = this.#badgeOption()?.dataset.custom === 'true';
    /** @type {HTMLElement[]} */
    const invalid = [];

    const group = this.placementGroup;
    const groupErr = /** @type {HTMLElement | null} */ (group?.querySelector('[data-hl-cz-error="placements"]') ?? null);
    const nothing = sel.length === 0;
    this.#setError(groupErr, group, nothing ? MSG.nothing : '');
    if (nothing && group) invalid.push(/** @type {HTMLElement} */ (group.querySelector('input:not(:disabled)') || group));

    if (this.logo) {
      let msg = this.#checkFile(this.logo, hasPlacement);
      if (msg === 'missing') msg = MSG.logo;
      this.#setError(this.querySelector('[data-hl-cz-error="logo"]'), this.logo, msg);
      if (msg) invalid.push(this.logo);
    }

    if (this.badgeFile) {
      let msg = customBadge ? this.#checkFile(this.badgeFile, true) : '';
      if (msg === 'missing') msg = MSG.badgeFile;
      this.#setError(this.querySelector('[data-hl-cz-error="badge-file"]'), this.badgeFile, msg);
      if (msg) invalid.push(this.badgeFile);
    }

    const formErr = /** @type {HTMLElement | null} */ (this.querySelector('[data-hl-cz-error="form"]'));
    this.#setError(formErr, null, focus && invalid.length ? MSG.form : '');

    invalid.sort((a, b) => (a.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1));
    if (focus && invalid[0]) {
      invalid[0].scrollIntoView({ block: 'center', behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
      invalid[0].focus({ preventScroll: true });
    }
    return invalid.length === 0;
  }

  /** @param {MouseEvent} e */
  #onClickCapture(e) {
    if (!this.active) return;
    const t = /** @type {HTMLElement} */ (e.target);
    if (!(t instanceof Element) || !this.section.contains(t)) return;
    const button = t.closest?.(
      'product-form-component [ref="addToCartButton"], sticky-add-to-cart [ref="addToCartButton"]'
    );
    if (!button) return;
    if (button.closest('product-form-component') && !this.form?.contains(button)) return;
    if (this.#busy || !this.#validate(true)) {
      e.preventDefault();
      e.stopImmediatePropagation();
      const main = /** @type {HTMLElement | null} */ (this.form?.querySelector('[ref="addToCartButton"]') ?? null);
      if (main) main.dataset.puppet = 'false';
    }
  }

  /** @param {SubmitEvent} e */
  #onSubmitCapture(e) {
    if (!this.active || e.target !== this.form) return;
    e.preventDefault();
    e.stopImmediatePropagation();
    if (this.#busy) return;
    if (!this.#validate(true)) {
      const main = /** @type {HTMLElement | null} */ (this.form?.querySelector('[ref="addToCartButton"]') ?? null);
      if (main) main.dataset.puppet = 'false';
      return;
    }
    this.#add();
  }

  #variantInfo() {
    const form = this.form;
    const id = /** @type {HTMLInputElement | null} */ (form?.querySelector('input[name="id"]'))?.value || '';
    let title = '';
    const json = this.section.querySelector('variant-picker script[type="application/json"]');
    try {
      const v = json?.textContent ? JSON.parse(json.textContent) : null;
      if (v && String(v.id) === String(id)) title = v.public_title || v.title || '';
    } catch {}
    if (!title) {
      title = [...this.section.querySelectorAll('variant-picker input:checked')]
        .map((i) => /** @type {HTMLInputElement} */ (i).value)
        .filter(Boolean)
        .join(' / ');
    }
    if (title === 'Default Title') title = '';
    return { id, title };
  }

  async #add() {
    const form = this.form;
    const productForm = /** @type {HTMLElement | null} */ (form?.closest('product-form-component') ?? null);
    if (!form || !productForm) return;

    const { id: variantId, title: variantTitle } = this.#variantInfo();
    if (!variantId) return;
    const quantity = this.quantity;
    const sel = this.selections;
    const group = `hl${Date.now().toString(36)}${Math.random().toString(36).slice(2, 7)}`;
    const productTitle = this.dataset.productTitle || '';
    const forLabel = variantTitle ? `${productTitle}, ${variantTitle}` : productTitle;

    const placements = sel.filter((s) => s.kind === 'placement').map((s) => s.title);
    const badge = sel.find((s) => s.kind === 'badge');
    const method = this.#methodLabel();
    const summary = [
      placements.length ? `${method}: ${placements.join('; ')}` : '',
      badge ? `Badge: ${badge.title}` : '',
    ]
      .filter(Boolean)
      .join('. ');

    const fd = new FormData();
    fd.append('items[0][id]', variantId);
    fd.append('items[0][quantity]', String(quantity));
    fd.append('items[0][properties][Branding]', summary);
    if (placements.length) fd.append('items[0][properties][Placement(s)]', `${method}: ${placements.join('; ')}`);
    if (badge) fd.append('items[0][properties][Badge]', badge.title);
    const notes = /** @type {HTMLTextAreaElement | null} */ (this.notes)?.value.trim();
    if (notes) fd.append('items[0][properties][Notes]', notes);
    const logoFile = /** @type {HTMLInputElement | null} */ (this.logo)?.files?.[0];
    if (logoFile) fd.append('items[0][properties][Logo]', logoFile, logoFile.name);
    const badgeFile = /** @type {HTMLInputElement | null} */ (this.badgeFile)?.files?.[0];
    if (badge && this.#badgeOption()?.dataset.custom === 'true' && badgeFile) {
      fd.append('items[0][properties][Badge artwork]', badgeFile, badgeFile.name);
    }
    fd.append('items[0][properties][_hl_group]', group);

    sel.forEach((s, i) => {
      const n = i + 1;
      fd.append(`items[${n}][id]`, s.id);
      fd.append(`items[${n}][quantity]`, String(quantity));
      fd.append(`items[${n}][parent_id]`, variantId);
      fd.append(`items[${n}][properties][For]`, forLabel);
      fd.append(`items[${n}][properties][_hl_group]`, group);
    });

    const sectionIds = [...document.querySelectorAll('cart-items-component')]
      .map((el) => /** @type {HTMLElement} */ (el).dataset.sectionId)
      .filter(Boolean);
    if (sectionIds.length) {
      fd.append('sections', sectionIds.join(','));
      fd.append('sections_url', window.location.pathname);
    }

    const itemCount = quantity * (1 + sel.length);
    const deferred = CartLinesUpdateEvent.createPromise();
    this.#busy = true;
    this.setAttribute('aria-busy', 'true');

    // One event, dispatched from inside the product form, so the drawer opens once.
    productForm.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: [
          { merchandiseId: variantId, quantity },
          ...sel.map((s) => ({ merchandiseId: s.id, quantity })),
        ],
        promise: deferred.promise,
      })
    );

    const fetchCart = () =>
      fetch(`${window.Theme?.routes?.cart_url || '/cart'}.js`, { headers: { Accept: 'application/json' } }).then((r) =>
        r.json()
      );

    try {
      const res = await fetch(window.Theme?.routes?.cart_add_url || '/cart/add.js', {
        method: 'POST',
        headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/javascript' },
        body: fd,
      });
      const data = await res.json();

      if (!res.ok || data.status) {
        const message = data.description || data.message || MSG.failed;
        productForm.dispatchEvent(
          new CartErrorEvent({ error: message, code: 'INVALID', detail: { description: data.description } })
        );
        const cart = await fetchCart().catch(() => null);
        deferred.resolve({
          cart: cart ? CartLinesUpdateEvent.createCartFromAjaxResponse(cart) : undefined,
          detail: { didError: true, items: cart?.items, source: 'hl-customise', itemCount },
        });
        this.#setError(this.querySelector('[data-hl-cz-error="form"]'), null, message);
        return;
      }

      const cart = await fetchCart();
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: 'product-form-component',
          sourceId: productForm.id,
          itemCount,
          productId: productForm.dataset.productId,
          sections: data.sections,
          didError: false,
        },
      });
      this.#setError(this.querySelector('[data-hl-cz-error="form"]'), null, '');
      const status = this.querySelector('[data-hl-cz-status]');
      if (status) status.textContent = MSG.added(quantity);
    } catch (error) {
      console.error(error);
      deferred.reject(error);
      productForm.dispatchEvent(
        new CartErrorEvent({ error: /** @type {Error} */ (error)?.message || MSG.failed, code: 'SERVICE_UNAVAILABLE' })
      );
      this.#setError(this.querySelector('[data-hl-cz-error="form"]'), null, MSG.failed);
    } finally {
      this.#busy = false;
      this.removeAttribute('aria-busy');
    }
  }
}

if (!customElements.get('hl-customise')) customElements.define('hl-customise', HlCustomise);
